import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DefinizioneFiltro, Opzione } from '../componenti/BarraFiltri.js';
import type { Ricerca } from './anagrafiche.js';
import { chiama } from './cliente.js';

/**
 * «Da confermare»: gli ordini da portare allo stato 2, per la produzione.
 *
 * Il servizio non manda importi di nessun tipo: qui non c'è niente da
 * nascondere, perché non arriva. Vedi `crm-api/src/produzione/rotte.ts`.
 */

export interface RigaDaConfermare {
  id: number;
  sku: string | null;
  bcNumber: string | null;
  description: string | null;
  crossReference: string | null;
  quantity: number;
}

export interface OrdineDaConfermare {
  id: number;
  orderNumber: string;
  orderDate: string;
  deliveryDateRequested: string | null;
  notes: string | null;
  customer: string;
  destination: string | null;
  destinationCity: string | null;
  destinationCountry: string | null;
  updatedAt: string;
  lines: RigaDaConfermare[];
}

export interface EsitoConferma {
  done: number;
  skipped: { id: number; reason: string }[];
}

/** Lo stato, la ricerca libera e i filtri, come li vuole il servizio. */
function parametri(stato: 1 | 2, ricerca: Pick<Ricerca, 'q' | 'filters'>): string {
  const p = new URLSearchParams({ state: String(stato) });
  if (ricerca.q.trim()) p.set('q', ricerca.q.trim());
  for (const [chiave, valore] of Object.entries(ricerca.filters)) {
    if (valore !== undefined && valore !== '') p.set(chiave, String(valore));
  }
  return p.toString();
}

export const useOrdiniDaConfermare = (stato: 1 | 2, ricerca: Pick<Ricerca, 'q' | 'filters'>) =>
  useQuery({
    queryKey: ['produzione', stato, ricerca.q, ricerca.filters],
    queryFn: () =>
      chiama<{ state: number; orders: OrdineDaConfermare[] }>(`/api/production/orders?${parametri(stato, ricerca)}`),
    // mentre si scrive nella ricerca resta l'elenco di prima, invece di sparire
    placeholderData: (prima) => prima,
  });

/** I filtri della pagina e i clienti fra cui scegliere. */
export const useFiltriProduzione = () =>
  useQuery({
    queryKey: ['produzione-filtri'],
    queryFn: () =>
      chiama<{ filters: DefinizioneFiltro[]; options: Record<string, Opzione[]> }>('/api/production/filters'),
    staleTime: 10 * 60_000,
  });

export function useConferma() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      chiama<EsitoConferma>('/api/production/orders/confirm', { metodo: 'POST', corpo: { ids } }),
    onSettled: () => client.invalidateQueries({ queryKey: ['produzione'] }),
  });
}
